import { useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { ExternalMedia, Image } from "@shared/graphql";
import Skeleton from "react-loading-skeleton";
import { IconCameraX } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { SkeletonableProps } from "../../tools/components";
import RatioImage from "../medias/ratio-image";
import Button from "../common/button";
import { GalleryHandler, Overlay } from "../layout/overlays";
import SpriteIcon from "../medias/sprite-icon";
import "./medias.scss";

const transDefs = defineMessages({
  video: { id: "article.gallery-video", defaultMessage: "{count, plural, =1 {Vidéo} other {Vidéos}}" },
  tour: { id: "article.gallery-tour", defaultMessage: "Tour" },
  gallery: { id: "article.gallery-link", defaultMessage: "{count, plural, =1 {# photo} other {# photos}}"}
});

type Props = {
  images: Image[];
  videos: ExternalMedia[];
  virtualVisits?: ExternalMedia[];
}

const MediasPart = ({ className, loading, data }: SkeletonableProps<Props>) => {

  const intl = useIntl();
  const galleryRef = useRef<GalleryHandler>(null);

  const wrapperClasses = classNames("images-part", className, {
    "no-image": !!data && data.images.length === 0
  });

  if (loading || !data)
    return (
      <div className={wrapperClasses}>
        <Skeleton containerClassName="previews" inline={true} count={5}
                  className="preview-img" borderRadius={0} />
      </div>);

  return (
    <div className={wrapperClasses}>
      <div className="previews">
        {data.images.length > 0
          ? data.images.slice(0, 5).map((img, index) =>
            <RatioImage key={index} className="preview-img" ratio="3/2" source={img.assets} />)
          : <IconCameraX className="x-icon" />
        }
      </div>
      <div className="more">
        {data.videos.length > 0 &&
          <Button className="white small lower with-icon"
                  onClick={() => galleryRef.current?.open(data.videos)}>
            <SpriteIcon hash="media.video" />
            {intl.formatMessage(transDefs.video, { count: data.videos.length })}
          </Button>
        }
        {(!!data.virtualVisits && data.virtualVisits.length > 0) &&
          <Button className="white small lower with-icon"
                  onClick={() => galleryRef.current?.open(data.virtualVisits!)}>
            <SpriteIcon hash="media.virtual-visit" width={20} />
            {intl.formatMessage(transDefs.tour)}
          </Button>
        }
        {data.images.length > 0 &&
          <Button className="white small lower with-icon"
                  onClick={() => galleryRef.current?.open(data.images)}>
            <SpriteIcon hash="media.picture" />
            {intl.formatMessage(transDefs.gallery, { count: data.images.length })}
          </Button>
        }
      </div>
      <Overlay.Gallery ref={galleryRef} />
    </div>);

}

export default MediasPart;
